import type {BigBagResumen, CambiosDeBigBag} from '@cnc/contracts';
import {useState} from 'react';
import {ErrorDeApi} from '../api/cliente';
import {useEditarBigBag, useFiltrosDeInventario} from '../api/inventario';
import {tr, trx} from '../i18n/idioma';
import {ayudaDeBigBag} from './ayuda-inventario';
import {NotaDeAyuda} from './nota-de-ayuda';
import {
  CAMPOS_BIG_BAG,
  convertirValor,
  GRUPOS_DE_CAMPOS as GRUPOS,
  opcionesDeListas,
  tipoDeEntrada,
  valorInicial,
  type ListaDeOpciones,
} from './campos-big-bag';

// Formulario para editar un Big Bag. Solo envia lo que cambio, con la version
// que se leyo; la API rechaza el cambio si otra persona lo edito antes.
export function EdicionDeBigBag({bolsa, onCerrar}: {bolsa: BigBagResumen; onCerrar: () => void}) {
  const editar = useEditarBigBag(bolsa.bbNo);
  const opciones = useFiltrosDeInventario({estado: 'todos'}).data;
  const todos = CAMPOS_BIG_BAG;
  const [valores, setValores] = useState<Record<string, string>>(() =>
    Object.fromEntries(todos.map(c => [c.campo, valorInicial(bolsa, c.campo)])),
  );
  const [error, setError] = useState<string | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);

  const guardar = async () => {
    setError(null);
    const cambios: Record<string, string | number | null> = {};
    for (const c of todos) {
      const nuevo = (valores[c.campo] ?? '').trim();
      if (nuevo === valorInicial(bolsa, c.campo)) continue;
      const convertido = convertirValor(c, nuevo);
      if ('error' in convertido) {
        setError(convertido.error);
        return;
      }
      if (c.tipo === 'numero' && convertido.valor === Number(valorInicial(bolsa, c.campo))) continue;
      cambios[c.campo] = convertido.valor;
    }
    if (!Object.keys(cambios).length) return onCerrar();
    try {
      const resultado = await editar.mutateAsync({version: bolsa.version, cambios: cambios as CambiosDeBigBag});
      if (resultado.avisos.length) setAvisos(resultado.avisos);
      else onCerrar();
    } catch (e) {
      setError(
        e instanceof ErrorDeApi
          ? [e.message, ...(e.detalles ?? [])].join(' ')
          : tr('No se pudo guardar.', 'Could not save.'),
      );
    }
  };

  const listas = opcionesDeListas(opciones);

  return (
    <form
      aria-label={tr('Editar Big Bag', 'Edit Big Bag')}
      className="mt-4 rounded-lg border border-verde/40 bg-white p-5"
      onSubmit={e => {
        e.preventDefault();
        void guardar();
      }}
    >
      <h2 className="font-medium">{tr(`Editar Big Bag ${bolsa.bbNo}`, `Edit Big Bag ${bolsa.bbNo}`)}</h2>
      {bolsa.estado === 'exported' && (
        <p className="mt-2 rounded-md bg-dorado-claro px-3 py-2 text-sm">
          {tr(
            'Este Big Bag ya fue exportado: sus datos salieron en los documentos de aduana. Un cambio aquí cambia los documentos que se generen de nuevo.',
            'This Big Bag has already been exported: its data went into the customs documents. A change here changes any documents generated again.',
          )}
        </p>
      )}
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        {GRUPOS.map(grupo => (
          <fieldset key={trx(grupo.titulo)} className="space-y-2">
            <legend className="text-sm font-medium text-apagado">{trx(grupo.titulo)}</legend>
            {grupo.campos.map(c => (
              <label key={c.campo} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-apagado">
                  {trx(c.etiqueta)}
                  {ayudaDeBigBag(c.campo) && (
                    <NotaDeAyuda titulo={c.etiqueta} texto={ayudaDeBigBag(c.campo)!} dentroDeEtiqueta />
                  )}
                </span>
                <input
                  aria-label={trx(c.etiqueta)}
                  type={tipoDeEntrada(c.tipo)}
                  inputMode={c.tipo === 'numero' ? 'decimal' : undefined}
                  list={c.lista ? `opciones-${c.lista}` : undefined}
                  value={valores[c.campo] ?? ''}
                  onChange={e => setValores(v => ({...v, [c.campo]: e.target.value}))}
                  className="w-48 rounded-md border border-linea px-2 py-1 text-right tabular-nums"
                />
              </label>
            ))}
          </fieldset>
        ))}
      </div>
      {(Object.keys(listas) as ListaDeOpciones[]).map(clave => (
        <datalist key={clave} id={`opciones-${clave}`}>
          {listas[clave].map(v => (
            <option key={v} value={v} />
          ))}
        </datalist>
      ))}
      <p className="mt-3 text-xs text-apagado">
        {tr(
          'Las onzas se recalculan de los gramos. Deja un campo vacío para borrar el dato. Mina, proveedor, exportador, planta y bodega se eligen de la lista; si escribes uno nuevo, se crea.',
          'Ounces are recalculated from grams. Leave a field empty to clear it. Mine, supplier, exporter, plant and warehouse are picked from the list; typing a new one creates it.',
        )}
      </p>
      {error && (
        <p className="mt-2 text-sm text-alerta" role="alert">
          {error}
        </p>
      )}
      {avisos.length > 0 && (
        <div className="mt-2 rounded-md bg-dorado-claro px-3 py-2 text-sm" role="status">
          <p className="font-medium">{tr('Guardado, con avisos:', 'Saved, with warnings:')}</p>
          <ul className="list-disc pl-5">
            {avisos.map(a => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        {avisos.length ? (
          <button type="button" className="rounded-md bg-verde px-3 py-1.5 text-sm text-white" onClick={onCerrar}>
            {tr('Listo', 'Done')}
          </button>
        ) : (
          <>
            <button
              type="submit"
              disabled={editar.isPending}
              className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              {tr('Guardar cambios', 'Save changes')}
            </button>
            <button
              type="button"
              onClick={onCerrar}
              className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
            >
              {tr('Cancelar', 'Cancel')}
            </button>
          </>
        )}
      </div>
    </form>
  );
}
